"use client";

import { Dispatch, Fragment, SetStateAction, useState } from "react";
import { useRouter } from "next/navigation";
import { Dialog, Transition } from "@headlessui/react";
import { Character } from "schema";

import { trpc } from "~/app/providers";
import { Act } from "../page";

export default function CopyPlayModal({
  playInfo,
  acts,
  setLoading,
}: {
  playInfo: {
    id: number;
    author: string;
    title: string;
    characters: Character[];
  };
  acts: Act[];
  setLoading: Dispatch<SetStateAction<boolean>>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [checkboxState, setCheckboxState] = useState<Act[]>(
    acts.map((act) => ({
      ...act,
      checked: false,
      scenes: act.scenes.map((scene) => ({
        ...scene,
        checked: false,
      })),
    })),
  );
  const [newPlayName, setNewPlayName] = useState("");

  const handleActChange = (actId: number, isChecked: boolean) => {
    setCheckboxState((prev) =>
      prev.map((act) =>
        act.bucketId === actId
          ? {
              ...act,
              checked: isChecked,
              scenes: act.scenes.map((scene) => ({
                ...scene,
                checked: isChecked,
              })),
            }
          : act,
      ),
    );
  };

  const handleSceneChange = (
    actId: number,
    sceneId: number,
    isChecked: boolean,
  ) => {
    setCheckboxState((prev) =>
      prev.map((act) =>
        act.bucketId === actId
          ? {
              ...act,
              scenes: act.scenes.map((scene) =>
                scene.bucketId === sceneId
                  ? { ...scene, checked: isChecked }
                  : scene,
              ),
            }
          : act,
      ),
    );
  };

  const router = useRouter();

  const copyPlay = trpc.plays.copyPlay.useMutation({
    onSuccess: () => {
      router.push("/inaction");
      router.refresh();
    },
  });

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  const handleClick = () => {
    const data = checkboxState.filter((item) => item.checked === true);
    if (data.length === 0) {
      alert("Must select at least 1 act or scene");
      return;
    }

    const invalidAct = data.some(
      (act) =>
        act.scenes.length > 0 && !act.scenes.some((scene) => scene.checked),
    );
    if (invalidAct) {
      alert("If an act has scenes, at least one scene must be selected");
      return;
    }

    if (!newPlayName) {
      alert("Must give a name for the Copied Play");
      return;
    }

    closeModal();
    setLoading(true);

    const filteredData = data.map((act) => ({
      head: act.bucketId,
      tail: act.tail,
      scenes: act.scenes
        .filter((scene) => scene.checked)
        .map((scene) => ({
          head: scene.bucketId,
          tail: scene.tail,
        })),
    }));

    copyPlay.mutate({
      playId: playInfo.id,
      playAuthor: playInfo.author,
      playTitle: newPlayName,
      acts: filteredData,
      characters: playInfo.characters,
    });
  };

  return (
    <>
      <button
        className="flex gap-1 p-2 text-sm font-medium text-gray-600  hover:text-gray-900"
        onClick={openModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
          />
        </svg>
        Copy
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mx-auto flex flex-col items-center">
                    <fieldset>
                      <legend className="text-lg font-medium text-gray-900">
                        Copy Play
                      </legend>

                      <p className="mt-1 text-pretty text-sm text-gray-700">
                        Select and copy specific Acts and Scenes, with just a
                        click
                      </p>

                      <div className="mt-8 space-y-2">
                        <div>
                          {checkboxState.map((act) => (
                            <div key={act.bucketId} className="act-container">
                              <label
                                htmlFor={`act-${act.bucketId}`}
                                className="flex cursor-pointer items-start gap-4"
                              >
                                <div className="flex items-center">
                                  &#8203;
                                  <input
                                    type="checkbox"
                                    className="size-4 rounded border-gray-300"
                                    id={`act-${act.bucketId}`}
                                    checked={act.checked}
                                    onChange={(e) =>
                                      handleActChange(
                                        act.bucketId,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </div>
                                <div>
                                  <strong className="font-medium text-gray-900">
                                    {act.lineText}
                                  </strong>
                                </div>
                              </label>

                              <div className="scenes-container pl-6">
                                {act.scenes.map((scene) => (
                                  <label
                                    key={scene.bucketId}
                                    htmlFor={`scene-${scene.bucketId}`}
                                    className="flex cursor-pointer items-start gap-4"
                                  >
                                    <div className="flex items-center">
                                      &#8203;
                                      <input
                                        type="checkbox"
                                        className="size-4 rounded border-gray-300"
                                        id={`scene-${scene.bucketId}`}
                                        checked={scene.checked}
                                        onChange={(e) =>
                                          handleSceneChange(
                                            act.bucketId,
                                            scene.bucketId,
                                            e.target.checked,
                                          )
                                        }
                                      />
                                    </div>
                                    <div>
                                      <strong className="font-medium text-gray-900">
                                        {scene.lineText}
                                      </strong>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </fieldset>
                    <>
                      {" "}
                      <label
                        htmlFor="Play Name"
                        className="relative my-6 block rounded-md border border-gray-200 shadow-sm focus-within:border-blue-600 focus-within:ring-1 focus-within:ring-blue-600"
                      >
                        <input
                          type="text"
                          id="Play Name"
                          className="peer border-none bg-transparent placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0"
                          placeholder="Play Name"
                          onChange={(e) => setNewPlayName(e.target.value)}
                        />

                        <span className="pointer-events-none absolute start-2.5 top-0 -translate-y-1/2 bg-white p-0.5 text-xs text-gray-700 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-0 peer-focus:text-xs">
                          Play Name
                        </span>
                      </label>
                    </>

                    <button
                      onClick={handleClick}
                      className="flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-3 text-indigo-600 duration-150 hover:bg-indigo-100 active:bg-indigo-200"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                        />
                      </svg>
                      Copy
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
